import * as XLSX from 'xlsx'

export interface FacilitiesInspectionRecord {
    'Inspection Type': string
    Frequency: string
    'Unit Name': string
    'Area Name': string
    'Inventory Item': string
    Description: string
}

/**
 * Generates a sample facilities inspection template Excel file
 * @returns Blob containing the Excel file
 */
export const generateFacilitiesInspectionTemplate = (): Blob => {
    // Sample data for the template based on the CSV format
    const sampleData: FacilitiesInspectionRecord[] = [
        {
            'Inspection Type': 'Common area furniture',
            Frequency: 'Weekly',
            'Unit Name': 'Common area furniture',
            'Area Name': 'Common area furniture',
            'Inventory Item': 'Furniture Inspect',
            Description:
                'Check for any signs of wear or fraying on soft goods, any chipping or discoloration on hard goods. Check all outdoor furniture for tears or damage and safety',
        },
        {
            'Inspection Type': 'Handrails',
            Frequency: 'Monthly',
            'Unit Name': 'Handrails',
            'Area Name': 'Handrails',
            'Inventory Item': 'Handrail Safety Check',
            Description: 'Check for any loose connections or wearing of paint',
        },
        {
            'Inspection Type': 'Elevators',
            Frequency: 'Monthly',
            'Unit Name': 'Elevators',
            'Area Name': 'Elevators',
            'Inventory Item': 'Elevator Vendor PM',
            Description: 'Regular PM work from vendor',
        },
        {
            'Inspection Type': 'Landscaping',
            Frequency: 'Weekly',
            'Unit Name': 'Landscaping',
            'Area Name': 'Landscaping',
            'Inventory Item': 'Mow',
            Description: 'Mow lawns and trim edges.',
        },
        {
            'Inspection Type': 'Landscaping',
            Frequency: 'Weekly',
            'Unit Name': 'Landscaping',
            'Area Name': 'Landscaping',
            'Inventory Item': 'Weeding',
            Description:
                'Remove weeds from garden beds, walkways, and parking areas.',
        },
        {
            'Inspection Type': 'Landscaping',
            Frequency: 'Weekly',
            'Unit Name': 'Landscaping',
            'Area Name': 'Landscaping',
            'Inventory Item': 'Irrigation Check',
            Description:
                'Inspect irrigation systems for leaks, clogs, or misaligned sprinkler heads; adjust as needed.',
        },
        {
            'Inspection Type': 'Landscaping',
            Frequency: 'Weekly',
            'Unit Name': 'Landscaping',
            'Area Name': 'Landscaping',
            'Inventory Item': 'Debris Sweep',
            Description:
                'Collect and dispose of litter, leaves, and debris from common areas',
        },
        {
            'Inspection Type': 'Landscaping',
            Frequency: 'Monthly',
            'Unit Name': 'Landscaping',
            'Area Name': 'Landscaping',
            'Inventory Item': 'Pruning',
            Description:
                'Prune shrubs and small trees to maintain shape and prevent overgrowth.',
        },
        {
            'Inspection Type': 'Landscaping',
            Frequency: 'Monthly',
            'Unit Name': 'Landscaping',
            'Area Name': 'Landscaping',
            'Inventory Item': 'Mulch Check',
            Description:
                'Check mulch levels in garden beds; replenish if necessary.',
        },
        {
            'Inspection Type': 'Landscaping',
            Frequency: 'Quarterly',
            'Unit Name': 'Landscaping',
            'Area Name': 'Landscaping',
            'Inventory Item': 'Fertilizing',
            Description:
                'Fertilize lawns, plants, and trees according to seasonal needs.',
        },
        {
            'Inspection Type': 'Landscaping',
            Frequency: 'Quarterly',
            'Unit Name': 'Landscaping',
            'Area Name': 'Landscaping',
            'Inventory Item': 'Tree Check',
            Description:
                'Inspect trees for signs of disease, pest infestation, or structural weakness; consult an arborist if needed',
        },
        {
            'Inspection Type': 'Landscaping',
            Frequency: 'Annual',
            'Unit Name': 'Landscaping',
            'Area Name': 'Landscaping',
            'Inventory Item': 'Aeration',
            Description: 'Perform deep aeration and overseeding of lawns.',
        },
        {
            'Inspection Type': 'Landscaping',
            Frequency: 'Annual',
            'Unit Name': 'Landscaping',
            'Area Name': 'Landscaping',
            'Inventory Item': 'Tree Trimming',
            Description:
                'Trim large trees near buildings or power lines (hire certified arborists).',
        },
    ]

    // Create workbook
    const workbook = XLSX.utils.book_new()

    // Create worksheet from sample data
    const worksheet = XLSX.utils.json_to_sheet(sampleData)

    // Set column widths for better readability
    const columnWidths = [
        { wch: 25 }, // Inspection Type
        { wch: 12 }, // Frequency
        { wch: 25 }, // Unit Name
        { wch: 25 }, // Area Name
        { wch: 25 }, // Inventory Item
        { wch: 60 }, // Description
    ]
    worksheet['!cols'] = columnWidths

    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Facilities Inspection')

    // Generate the Excel file as a blob
    const excelBuffer = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
    })
    return new Blob([excelBuffer], {
        type:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
}

/**
 * Generates a facilities inspection template with dropdown instructions
 * @returns Blob containing the Excel file with instruction sheet
 */
export const generateFacilitiesInspectionTemplateWithInstructions = (): Blob => {
    // Define the frequency options
    const frequencyOptions = [
        'Monthly',
        'Annual',
        'Quarterly',
        'Weekly',
        'Daily',
        'Semi-annual',
    ]

    // Create workbook
    const workbook = XLSX.utils.book_new()

    // Create the main worksheet with sample data
    const sampleData: FacilitiesInspectionRecord[] = [
        {
            'Inspection Type': 'Common area furniture',
            Frequency: 'Weekly',
            'Unit Name': 'Common area furniture',
            'Area Name': 'Common area furniture',
            'Inventory Item': 'Furniture Inspect',
            Description:
                'Check for any signs of wear or fraying on soft goods, any chipping or discoloration on hard goods. Check all outdoor furniture for tears or damage and safety',
        },
        {
            'Inspection Type': 'Handrails',
            Frequency: 'Monthly',
            'Unit Name': 'Handrails',
            'Area Name': 'Handrails',
            'Inventory Item': 'Handrail Safety Check',
            Description: 'Check for any loose connections or wearing of paint',
        },
        {
            'Inspection Type': 'Elevators',
            Frequency: 'Monthly',
            'Unit Name': 'Elevators',
            'Area Name': 'Elevators',
            'Inventory Item': 'Elevator Vendor PM',
            Description: 'Regular PM work from vendor',
        },
        {
            'Inspection Type': 'Landscaping',
            Frequency: 'Weekly',
            'Unit Name': 'Landscaping',
            'Area Name': 'Landscaping',
            'Inventory Item': 'Mow',
            Description: 'Mow lawns and trim edges.',
        },
        {
            'Inspection Type': 'Landscaping',
            Frequency: 'Weekly',
            'Unit Name': 'Landscaping',
            'Area Name': 'Landscaping',
            'Inventory Item': 'Weeding',
            Description:
                'Remove weeds from garden beds, walkways, and parking areas.',
        },
        {
            'Inspection Type': 'Landscaping',
            Frequency: 'Weekly',
            'Unit Name': 'Landscaping',
            'Area Name': 'Landscaping',
            'Inventory Item': 'Irrigation Check',
            Description:
                'Inspect irrigation systems for leaks, clogs, or misaligned sprinkler heads; adjust as needed.',
        },
        {
            'Inspection Type': 'Landscaping',
            Frequency: 'Weekly',
            'Unit Name': 'Landscaping',
            'Area Name': 'Landscaping',
            'Inventory Item': 'Debris Sweep',
            Description:
                'Collect and dispose of litter, leaves, and debris from common areas',
        },
        {
            'Inspection Type': 'Landscaping',
            Frequency: 'Monthly',
            'Unit Name': 'Landscaping',
            'Area Name': 'Landscaping',
            'Inventory Item': 'Pruning',
            Description:
                'Prune shrubs and small trees to maintain shape and prevent overgrowth.',
        },
        {
            'Inspection Type': 'Landscaping',
            Frequency: 'Monthly',
            'Unit Name': 'Landscaping',
            'Area Name': 'Landscaping',
            'Inventory Item': 'Mulch Check',
            Description:
                'Check mulch levels in garden beds; replenish if necessary.',
        },
        {
            'Inspection Type': 'Landscaping',
            Frequency: 'Quarterly',
            'Unit Name': 'Landscaping',
            'Area Name': 'Landscaping',
            'Inventory Item': 'Fertilizing',
            Description:
                'Fertilize lawns, plants, and trees according to seasonal needs.',
        },
        {
            'Inspection Type': 'Landscaping',
            Frequency: 'Quarterly',
            'Unit Name': 'Landscaping',
            'Area Name': 'Landscaping',
            'Inventory Item': 'Tree Check',
            Description:
                'Inspect trees for signs of disease, pest infestation, or structural weakness; consult an arborist if needed',
        },
        {
            'Inspection Type': 'Landscaping',
            Frequency: 'Annual',
            'Unit Name': 'Landscaping',
            'Area Name': 'Landscaping',
            'Inventory Item': 'Aeration',
            Description: 'Perform deep aeration and overseeding of lawns.',
        },
        {
            'Inspection Type': 'Landscaping',
            Frequency: 'Annual',
            'Unit Name': 'Landscaping',
            'Area Name': 'Landscaping',
            'Inventory Item': 'Tree Trimming',
            Description:
                'Trim large trees near buildings or power lines (hire certified arborists).',
        },
    ]

    const worksheet = XLSX.utils.json_to_sheet(sampleData)

    // Set column widths
    const columnWidths = [
        { wch: 25 }, // Inspection Type
        { wch: 12 }, // Frequency
        { wch: 25 }, // Unit Name
        { wch: 25 }, // Area Name
        { wch: 25 }, // Inventory Item
        { wch: 60 }, // Description
    ]
    worksheet['!cols'] = columnWidths

    // Create instruction sheet for setting up dropdowns
    const instructionData = [
        ['SETTING UP FREQUENCY DROPDOWNS'],
        [''],
        ['To add dropdown validation to the Frequency column:'],
        ['1. Select the Frequency column (column B)'],
        ['2. Go to Data > Data Validation'],
        ['3. Set Allow to "List"'],
        ['4. In the Source field, enter the following options:'],
        [''],
        ['Frequency Options:'],
        ...frequencyOptions.map((option) => [option]),
        [''],
        ['5. Check "In-cell dropdown"'],
        ['6. Click OK'],
        [''],
        ['This will create a dropdown with the predefined frequency options.'],
        [''],
        ['Available Frequency Options:'],
        ...frequencyOptions.map((option) => [option]),
    ]

    const instructionSheet = XLSX.utils.aoa_to_sheet(instructionData)

    // Set column width for instruction sheet
    instructionSheet['!cols'] = [{ wch: 50 }]

    // Add both sheets to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Facilities Inspection')
    XLSX.utils.book_append_sheet(
        workbook,
        instructionSheet,
        'Setup Instructions',
    )

    // Generate the Excel file as a blob
    const excelBuffer = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
    })
    return new Blob([excelBuffer], {
        type:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
}

/**
 * Downloads the facilities inspection template with setup instructions
 */
export const downloadFacilitiesInspectionTemplateWithInstructions = () => {
    const blob = generateFacilitiesInspectionTemplateWithInstructions()
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'facilities-inspection-template-with-instructions.xlsx'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
}

/**
 * Downloads the facilities inspection template
 */
export const downloadFacilitiesInspectionTemplate = () => {
    const blob = generateFacilitiesInspectionTemplate()
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'facilities-inspection-template.xlsx'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
}
